import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Eye, Play, ShoppingCart, CreditCard, DollarSign,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Loader2, Sparkles, AlertTriangle, Info, CheckCircle,
  BarChart3, Target, Users, Package, ExternalLink
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '365d', days: 365 },
];

const METRIC_OPTIONS = [
  { key: 'views', label: 'Views', color: '#1c1917' },
  { key: 'previews', label: 'Previews', color: '#B49A88' },
  { key: 'checkouts', label: 'Checkouts', color: '#78716c' },
  { key: 'purchases', label: 'Purchases', color: '#57534e' },
];

const GrowthBadge = ({ pct }) => {
  if (pct === 0 || pct === undefined || pct === null) return <span className="text-[10px] text-stone-400">—</span>;
  const up = pct > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      <Icon className="w-3 h-3" />{Math.abs(pct)}%
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, growth, sub, testId }) => (
  <div data-testid={testId} className="bg-white border border-stone-100 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className="w-3.5 h-3.5 text-stone-400" />
      <span className="text-[10px] font-medium text-stone-400 uppercase tracking-wider">{label}</span>
    </div>
    <div className="flex items-end gap-2">
      <span className="text-lg font-serif font-bold text-stone-800">{value}</span>
      {growth !== undefined && <GrowthBadge pct={growth} />}
    </div>
    {sub && <p className="text-[10px] text-stone-400 mt-0.5">{sub}</p>}
  </div>
);

const FunnelStep = ({ step, count, rate, drop, isLast, maxCount }) => {
  const barWidth = maxCount > 0 ? Math.max((count / maxCount) * 100, 2) : 2;
  return (
    <div data-testid={`funnel-${step.toLowerCase()}`} className="flex items-center gap-3">
      <div className="w-20 text-right flex-shrink-0">
        <p className="text-sm font-semibold text-stone-800">{count.toLocaleString()}</p>
        <p className="text-[10px] text-stone-400">{step}</p>
      </div>
      <div className="flex-1">
        <div className="h-7 bg-stone-50 rounded-lg overflow-hidden">
          <div className="h-full bg-stone-800 rounded-lg transition-all duration-700" style={{ width: `${barWidth}%` }} />
        </div>
      </div>
      <div className="w-16 text-right flex-shrink-0">
        {!isLast && drop > 0 ? (
          <p className="text-[10px] text-rose-400">-{drop}%</p>
        ) : rate > 0 ? (
          <p className="text-[10px] text-stone-500">{rate}%</p>
        ) : null}
      </div>
    </div>
  );
};

const InsightCard = ({ insight }) => {
  const config = {
    positive: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    info: { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' },
  }[insight.type] || { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' };
  const Icon = config.icon;
  return (
    <div data-testid={`insight-${insight.type}`} className={`flex gap-3 p-3 rounded-lg border ${config.bg} ${config.border}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
      <div>
        <p className="text-xs font-semibold text-stone-700">{insight.title}</p>
        <p className="text-[11px] text-stone-500 mt-0.5">{insight.detail}</p>
      </div>
    </div>
  );
};

const BenchmarkRow = ({ label, value, median, vs }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0">
    <span className="text-xs text-stone-500">{label}</span>
    <div className="flex items-center gap-3">
      <span className="text-xs font-semibold text-stone-800">{value}</span>
      <span className="text-[10px] text-stone-400">median {median}</span>
      {vs !== 0 && vs !== undefined && (
        <span className={`text-[10px] font-semibold ${vs > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
          {vs > 0 ? '+' : ''}{vs}%
        </span>
      )}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-100 rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-medium text-stone-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}><span className="font-semibold">{p.name}:</span> {p.value}</p>
      ))}
    </div>
  );
};

const SectionHeading = ({ children, testId }) => (
  <h3 data-testid={testId} className="text-sm font-sans font-bold text-stone-600 uppercase tracking-wider mb-4">{children}</h3>
);

const EmptySection = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-10 h-10 rounded-full bg-stone-50 flex items-center justify-center mb-3">
      <Package className="w-4 h-4 text-stone-300" />
    </div>
    <p className="text-xs text-stone-400">{message}</p>
  </div>
);

/* ═══════════════════════════════════════════════════ */
const ProductDrillDown = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [days, setDays] = useState(30);
  const [activeMetrics, setActiveMetrics] = useState(['views', 'purchases']);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/admin/products/${slug}?days=${d}`);
      setData(res.data);
    } catch (err) {
      console.error('Product drilldown error:', err);
      setError(true);
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF8] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto mb-3" />
          <p className="text-xs text-stone-400">Loading product analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="product-not-found" className="min-h-screen bg-[#FDFBF8] flex flex-col items-center justify-center gap-5">
        <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center">
          <Package className="w-6 h-6 text-stone-400" />
        </div>
        <div className="text-center">
          <h2 className="font-serif text-lg font-bold text-stone-700 mb-1">Product not found</h2>
          <p className="text-xs text-stone-400 max-w-xs">The product <span className="font-mono text-stone-500">{slug}</span> does not exist in the catalog or has been removed.</p>
        </div>
        <Button data-testid="back-to-admin-404" variant="outline" onClick={() => navigate('/admin')} className="rounded-full text-xs">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back to Admin
        </Button>
      </div>
    );
  }

  const { overview: ov, funnel, growth: g, recommendations: rec, benchmarks: bm, insights, also_bought, time_series: ts } = data;
  const maxFunnel = Math.max(...funnel.map(f => f.count), 1);
  const hasActivity = ov.views > 0 || ov.orders > 0 || ov.checkouts > 0 || ov.previews > 0;
  const hasRevenue = ov.revenue.usd.revenue > 0 || ov.revenue.nok.revenue > 0;
  const hasRecData = rec.impressions > 0 || rec.clicks > 0;

  const toggleMetric = (key) => {
    setActiveMetrics(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF8]" data-testid="product-drilldown">
      {/* Sticky nav */}
      <nav className="border-b border-stone-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between h-12">
          <button data-testid="back-to-admin" onClick={() => navigate('/admin')} className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs font-medium hidden sm:inline">Admin</span>
          </button>
          <div className="flex items-center gap-1 bg-stone-50 rounded-lg p-0.5">
            {PERIODS.map(p => (
              <button key={p.days} data-testid={`pd-period-${p.days}`} onClick={() => setDays(p.days)}
                className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${days === p.days ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 data-testid="pd-title" className="text-xl md:text-2xl font-serif font-bold text-stone-800 truncate">{data.title}</h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">{data.category}</span>
            <span className="text-stone-200">·</span>
            <span className="text-[10px] text-stone-400">${data.price_usd} / {data.price_nok} kr</span>
            <span className="text-stone-200">·</span>
            {data.active ? (
              <span className="text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-medium">Active</span>
            ) : (
              <span className="text-[9px] bg-stone-100 text-stone-400 px-1.5 py-0.5 rounded-full font-medium">Inactive</span>
            )}
            <span className="text-stone-200">·</span>
            <span className="text-[10px] text-stone-400">Last {data.period_days} days</span>
          </div>
        </div>

        {/* No activity empty state */}
        {!hasActivity && (
          <div data-testid="pd-empty-state" className="bg-white border border-stone-100 rounded-xl p-10 text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-stone-50 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-6 h-6 text-stone-300" />
            </div>
            <h3 className="font-serif text-base font-bold text-stone-700 mb-1">No activity yet</h3>
            <p className="text-xs text-stone-400 max-w-sm mx-auto">
              This product has no views, previews, or purchases in the selected period. Try expanding the time range or check back later.
            </p>
          </div>
        )}

        {/* KPI Overview */}
        {hasActivity && (
          <>
            <SectionHeading testId="pd-kpi-heading">Overview</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              <StatCard testId="pd-views" icon={Eye} label="Views" value={ov.views.toLocaleString()} growth={g.views.pct} sub={`${ov.unique_viewers} unique`} />
              <StatCard testId="pd-previews" icon={Play} label="Previews" value={ov.previews.toLocaleString()} growth={g.previews.pct} />
              <StatCard testId="pd-orders" icon={ShoppingCart} label="Orders" value={ov.orders} growth={g.orders.pct} />
              <StatCard testId="pd-conversion" icon={Target} label="Conversion" value={`${ov.conversion}%`} growth={g.conversion.pct} />
            </div>
          </>
        )}

        {/* Revenue & Orders — NOK / USD split */}
        {hasActivity && (
          <>
            <SectionHeading testId="pd-revenue-heading">Revenue & Orders</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
              <div data-testid="pd-rev-usd" className="bg-white border border-stone-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-[10px] font-medium text-stone-400 uppercase tracking-wider">Revenue (USD)</span>
                </div>
                <span className="text-lg font-serif font-bold text-stone-800">${ov.revenue.usd.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                <div className="flex items-center gap-2 mt-1">
                  <GrowthBadge pct={g.revenue_usd.pct} />
                  <span className="text-[10px] text-stone-400">{ov.revenue.usd.count} orders</span>
                </div>
              </div>
              <div data-testid="pd-rev-nok" className="bg-white border border-stone-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-[10px] font-medium text-stone-400 uppercase tracking-wider">Revenue (NOK)</span>
                </div>
                <span className="text-lg font-serif font-bold text-stone-800">{ov.revenue.nok.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span>
                <p className="text-[10px] text-stone-400 mt-1">{ov.revenue.nok.count} orders</p>
              </div>
              <StatCard testId="pd-aov" icon={BarChart3} label="AOV (USD)" value={`$${ov.aov_usd}`} />
              <StatCard testId="pd-checkouts" icon={CreditCard} label="Checkouts" value={ov.checkouts} growth={g.checkouts.pct} sub={`${ov.unique_viewers} unique viewers`} />
            </div>
          </>
        )}

        {/* Funnel */}
        {hasActivity && (
          <div className="bg-white border border-stone-100 rounded-xl p-5 mb-10">
            <SectionHeading testId="pd-funnel-heading">Product Funnel</SectionHeading>
            <div className="space-y-3">
              {funnel.map((f, i) => (
                <FunnelStep key={f.step} {...f} isLast={i === funnel.length - 1} maxCount={maxFunnel} />
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-stone-50 flex items-center justify-between text-[10px] text-stone-400">
              <span>Overall: View → Purchase {ov.conversion}%</span>
              <span>{ov.orders} paid of {ov.checkouts} checkouts</span>
            </div>
          </div>
        )}

        {/* Performance Over Time */}
        {hasActivity && (
          <div className="bg-white border border-stone-100 rounded-xl p-5 mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <SectionHeading testId="pd-chart-heading">Performance Over Time</SectionHeading>
              <div className="flex gap-1 flex-wrap">
                {METRIC_OPTIONS.map(m => (
                  <button key={m.key} data-testid={`pd-metric-toggle-${m.key}`} onClick={() => toggleMetric(m.key)}
                    className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all border ${activeMetrics.includes(m.key) ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-400 border-stone-200 hover:border-stone-300'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {ts && ts.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={ts} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#a8a29e' }} />
                  <Tooltip content={<ChartTooltip />} />
                  {METRIC_OPTIONS.filter(m => activeMetrics.includes(m.key)).map(m => (
                    <Area key={m.key} type="monotone" dataKey={m.key} name={m.label} stroke={m.color} fill="none" strokeWidth={2} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptySection message="No time series data for this period" />
            )}
          </div>
        )}

        {/* Two column: Recommendations + Benchmarks/Also-Bought */}
        {hasActivity && (
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Recommendation Performance */}
            <div className="bg-white border border-stone-100 rounded-xl p-5">
              <SectionHeading testId="pd-rec-heading">Recommendation Performance</SectionHeading>
              {hasRecData ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <StatCard testId="pd-rec-imp" icon={Eye} label="Impressions" value={rec.impressions} />
                    <StatCard testId="pd-rec-clicks" icon={Sparkles} label="Clicks" value={rec.clicks} />
                    <StatCard testId="pd-rec-ctr" icon={Target} label="CTR" value={`${rec.ctr}%`} />
                    <StatCard testId="pd-rec-rev" icon={DollarSign} label="Revenue" value={rec.revenue > 0 ? `$${rec.revenue}` : '$0'} />
                  </div>
                  <div className="flex justify-between text-[10px] text-stone-400 mt-3 pt-3 border-t border-stone-50">
                    <span>Checkouts: {rec.checkouts}</span>
                    <span>Purchases: {rec.purchases}</span>
                    <span>Conv: {rec.conversion}%</span>
                  </div>
                </>
              ) : (
                <EmptySection message="No recommendation data yet. This product hasn't been shown in recommendations during this period." />
              )}
            </div>

            {/* Benchmarks + Also bought */}
            <div className="space-y-4">
              {/* Benchmarks */}
              <div className="bg-white border border-stone-100 rounded-xl p-5">
                <SectionHeading testId="pd-bench-heading">vs Catalog</SectionHeading>
                <BenchmarkRow label="Views" value={bm.views.value} median={bm.views.median} vs={bm.views.vs_median} />
                <BenchmarkRow label="Conversion" value={`${bm.conversion.value}%`} median={`${bm.conversion.median}%`} vs={bm.conversion.vs_median} />
                <BenchmarkRow label="Revenue (USD)" value={`$${bm.revenue_usd.value}`} median={`$${bm.revenue_usd.median}`} vs={bm.revenue_usd.vs_median} />
              </div>

              {/* Also bought */}
              {also_bought.length > 0 && (
                <div className="bg-white border border-stone-100 rounded-xl p-5">
                  <SectionHeading testId="pd-also-bought-heading">Also Purchased</SectionHeading>
                  {also_bought.map(ab => (
                    <div key={ab.slug} className="flex justify-between items-center py-2 border-b border-stone-50 last:border-0">
                      <button
                        data-testid={`also-bought-${ab.slug}`}
                        onClick={() => navigate(`/admin/products/${ab.slug}`)}
                        className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors"
                      >
                        {ab.slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </button>
                      <span className="text-[10px] text-stone-400">{ab.purchases} orders</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Insights */}
        {insights.length > 0 && (
          <div className="mb-10">
            <SectionHeading testId="pd-insights-heading">Insights</SectionHeading>
            <div className="space-y-2">
              {insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-6 border-t border-stone-100">
          <p className="text-[10px] text-stone-400 text-center">
            Only completed/paid transactions counted. NOK and USD shown separately. Recommendation attribution uses a 30-minute click window.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductDrillDown;
